import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Circle, GoogleMap, Marker } from '@react-google-maps/api';
import { debounce } from 'lodash';
import styled, { css } from 'styled-components';

import { theme } from 'assets/styles/theme';
import { makeTransparentColor } from 'assets/styles/transparentColor';
import mapLocationIcon from 'assets/svg/map-location.svg';
import pickupPointerIcon from 'assets/svg/pickup-pointer.svg';
import RedButton from 'components/buttons/RedButton';
import { locationMapStyle } from 'components/location/picker/locationMapStyle';
import { H4 } from 'components/typography/Headers';
import { Text1, Text2 } from 'components/typography/Texts';
import { useAlertPopup } from 'hooks/useAlertPopup';
import { useGoogleMapsApi } from 'hooks/useGoogleMapsApi';
import {
  fetchPlaceDetails,
  fetchPlacePredictions,
  MapPrediction,
  reverseGeocode,
} from 'services/utils/location/googlePlacesWebService';
import {
  AUTOCOMPLETE_DEBOUNCE_MS,
  AUTOCOMPLETE_MIN_LENGTH,
  calculateRegionZoom,
  DEFAULT_LOCATION,
  MILE_TO_METERS,
} from 'services/utils/location/locationPicker.constants';
import { formatLocationLabel, isSupportedCountry } from 'services/utils/location/locationPicker.utils';
import { LocationDetailsDto } from 'services/utils/location/locationService.dto';

const Container = styled.div<{ $variant: 'popup' | 'embedded' }>`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 16px;
  width: 100%;
  box-sizing: border-box;

  ${props =>
    props.$variant === 'popup' &&
    css`
      width: min(640px, 90vw);
      max-width: 90vw;
      margin: 0 auto;
      padding: 52px 24px 24px;

      ${theme.mq.phone} {
        width: 100%;
        padding: 52px 16px 16px;
      }
    `}
`;

const SearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  background: ${theme.color.lighterGray};
  border-radius: 6px;
  padding: 11px 15px;
  box-sizing: border-box;
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 16px;
  letter-spacing: -0.4px;
  line-height: 22px;
  min-width: 0;

  &::placeholder {
    color: ${theme.color.placeholderGrey};
  }
`;

const GpsButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${theme.color.messageGray};
  flex-shrink: 0;
  padding: 0 0 0 8px;
`;

const GpsIcon = styled.img`
  width: 20px;
  height: 20px;
`;

const PredictionsList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 180px;
  overflow-y: auto;
  border: 1px solid ${theme.color.borderGray};
  border-radius: ${theme.borderRadiusStandard}px;
  text-align: left;
`;

const PredictionItem = styled.li`
  padding: 10px 12px;
  cursor: pointer;

  &:hover {
    background: ${theme.color.lighterGray};
  }
`;

const MapContainer = styled.div<{ $variant: 'popup' | 'embedded' }>`
  width: 100%;
  border-radius: ${theme.borderRadiusStandard}px;
  overflow: hidden;
  background: ${theme.color.lighterGray};

  ${props =>
    props.$variant === 'embedded'
      ? css`
          aspect-ratio: 4 / 5;
          height: min(340px, 38vh);
          min-height: 240px;

          ${theme.mq.phone} {
            height: min(280px, 36vh);
            min-height: 220px;
          }
        `
      : css`
          height: 320px;

          ${theme.mq.phone} {
            height: 260px;
          }
        `}
`;

const ConfirmButton = styled(RedButton)`
  height: 46px;
  flex-shrink: 0;
  margin-top: 8px;
`;

const mapContainerStyle = { width: '100%', height: '100%' };

interface SelectedLocation {
  coords: { lat: number; lng: number };
  details?: LocationDetailsDto;
}

interface Props {
  defaultLocation?: LocationDetailsDto;
  markerRadiusMiles?: number;
  buttonLabel?: string;
  variant?: 'popup' | 'embedded';
  hideConfirmButton?: boolean;
  onConfirm: (location: LocationDetailsDto) => void;
}

const toSelected = (location: LocationDetailsDto): SelectedLocation => ({
  coords: {
    lat: location.latitude || DEFAULT_LOCATION.latitude!,
    lng: location.longitude || DEFAULT_LOCATION.longitude!,
  },
  details: location,
});

export const LocationPicker = ({
  defaultLocation,
  markerRadiusMiles,
  buttonLabel,
  variant = 'popup',
  hideConfirmButton = false,
  onConfirm,
}: Props) => {
  const { t } = useTranslation();
  const { showAlertPopup } = useAlertPopup();
  const { isLoaded } = useGoogleMapsApi();

  const [selected, setSelected] = useState<SelectedLocation>(() => toSelected(defaultLocation || DEFAULT_LOCATION));
  const [inputValue, setInputValue] = useState(() => formatLocationLabel(defaultLocation));
  const [predictions, setPredictions] = useState<MapPrediction[]>([]);
  const [gpsLocation, setGpsLocation] = useState<LocationDetailsDto>();
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const geocodeRequestId = useRef(0);

  const isEmbedded = variant === 'embedded';
  const zoom = useMemo(
    () => (isEmbedded || !markerRadiusMiles ? 14 : calculateRegionZoom(markerRadiusMiles)),
    [isEmbedded, markerRadiusMiles]
  );

  useEffect(() => {
    if (!isLoaded || !navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      position => {
        const { latitude, longitude } = position.coords;
        reverseGeocode(latitude, longitude, 'GPS')
          .then(location => location && setGpsLocation(location))
          .catch(() => undefined);
      },
      () => undefined,
      { enableHighAccuracy: true, timeout: 15000 }
    );
  }, [isLoaded]);

  const fetchPredictions = useCallback(
    debounce(async (value: string, bias?: { lat: number; lng: number }) => {
      if (value.trim().length < AUTOCOMPLETE_MIN_LENGTH) {
        setPredictions([]);
        return;
      }
      try {
        const results = await fetchPlacePredictions(value, bias);
        setPredictions(results);
      } catch {
        setPredictions([]);
      }
    }, AUTOCOMPLETE_DEBOUNCE_MS),
    []
  );

  useEffect(() => () => fetchPredictions.cancel(), [fetchPredictions]);

  const moveMapTo = (coords: { lat: number; lng: number }) => {
    map?.panTo(coords);
    map?.setZoom(zoom);
  };

  const handleInputChange = (value: string) => {
    setInputValue(value);
    const bias = gpsLocation?.latitude ? { lat: gpsLocation.latitude, lng: gpsLocation.longitude! } : selected.coords;
    fetchPredictions(value, bias);
  };

  const handlePredictionSelect = async (prediction: MapPrediction) => {
    setPredictions([]);
    try {
      const details = await fetchPlaceDetails(prediction.placeId);
      const coords = { lat: details.latitude!, lng: details.longitude! };
      setSelected({ coords, details });
      setInputValue(formatLocationLabel(details));
      moveMapTo(coords);
    } catch {
      showAlertPopup({
        header: t('location-picker.not-found-title'),
        description: t('location-picker.not-found-description'),
        firstButton: { label: t('ok') },
      });
    }
  };

  const handleGpsPress = async () => {
    if (gpsLocation) {
      const coords = { lat: gpsLocation.latitude!, lng: gpsLocation.longitude! };
      setSelected({ coords, details: gpsLocation });
      setInputValue(formatLocationLabel(gpsLocation));
      moveMapTo(coords);
      return;
    }

    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      async position => {
        const { latitude, longitude } = position.coords;
        try {
          const location = await reverseGeocode(latitude, longitude, 'GPS');
          if (!location) {
            showAlertPopup({
              header: t('location-picker.not-found-title'),
              description: t('location-picker.not-found-description'),
              firstButton: { label: t('ok') },
            });
            return;
          }
          setGpsLocation(location);
          const coords = { lat: location.latitude!, lng: location.longitude! };
          setSelected({ coords, details: location });
          setInputValue(formatLocationLabel(location));
          moveMapTo(coords);
        } catch {
          showAlertPopup({
            header: t('location-picker.not-found-title'),
            description: t('location-picker.not-found-description'),
            firstButton: { label: t('ok') },
          });
        }
      },
      () => undefined,
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  const handleMapClick = async (event: google.maps.MapMouseEvent) => {
    if (!event.latLng) return;
    const coords = { lat: event.latLng.lat(), lng: event.latLng.lng() };
    const requestId = ++geocodeRequestId.current;
    setPredictions([]);
    setSelected({ coords, details: undefined });

    const location = await reverseGeocode(coords.lat, coords.lng, 'MAP');
    if (requestId !== geocodeRequestId.current) return;
    if (!location) return;
    setSelected({ coords, details: location });
    setInputValue(formatLocationLabel(location));
  };

  const handleConfirm = async () => {
    let result = selected.details;
    if (!result) {
      result = await reverseGeocode(selected.coords.lat, selected.coords.lng, 'GPS');
    }

    if (!result) {
      showAlertPopup({
        header: t('location-picker.not-found-title'),
        description: t('location-picker.not-found-description'),
        firstButton: { label: t('ok') },
      });
      return;
    }

    if (!isSupportedCountry(result)) {
      showAlertPopup({
        header: t('location-picker.limit-title'),
        description: t('location-picker.limit-description'),
        firstButton: { label: t('ok') },
      });
      return;
    }

    if (!result.zipCode) {
      showAlertPopup({
        header: t('location-picker.needed-title'),
        description: t('location-picker.needed-description'),
        firstButton: { label: t('ok') },
      });
      return;
    }

    onConfirm(result);
  };

  if (!isLoaded) {
    return (
      <Container $variant={variant} data-testid={'location-picker-loading'}>
        <Text1>{t('loading')}</Text1>
      </Container>
    );
  }

  return (
    <Container $variant={variant} data-testid={'location-picker'}>
      {variant === 'popup' && <H4 data-testid={'location-picker-header'}>{t('location-picker.header')}</H4>}
      <SearchRow>
        <SearchInput
          data-testid={'location-picker-search'}
          value={inputValue}
          placeholder={t('set-location')}
          onChange={event => handleInputChange(event.target.value)}
        />
        <GpsButton type='button' data-testid={'location-picker-gps'} onClick={handleGpsPress}>
          {gpsLocation?.city && <Text2>{gpsLocation.city}</Text2>}
          <GpsIcon src={pickupPointerIcon} alt='' />
        </GpsButton>
      </SearchRow>

      {predictions.length > 0 && (
        <PredictionsList data-testid={'location-picker-predictions'}>
          {predictions.map(prediction => (
            <PredictionItem
              key={prediction.placeId}
              data-testid={'location-picker-prediction'}
              onClick={() => handlePredictionSelect(prediction)}>
              {prediction.description}
            </PredictionItem>
          ))}
        </PredictionsList>
      )}

      <MapContainer $variant={variant}>
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={selected.coords}
          zoom={zoom}
          onLoad={setMap}
          onClick={handleMapClick}
          options={{
            styles: locationMapStyle,
            streetViewControl: false,
            fullscreenControl: true,
            mapTypeControl: true,
            mapTypeControlOptions: {
              style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
              position: google.maps.ControlPosition.TOP_LEFT,
              mapTypeIds: [google.maps.MapTypeId.ROADMAP, google.maps.MapTypeId.SATELLITE],
            },
            zoomControl: true,
            clickableIcons: false,
          }}>
          {!markerRadiusMiles && (
            <Marker
              position={selected.coords}
              icon={{
                url: mapLocationIcon,
                scaledSize: new google.maps.Size(35, 35),
                anchor: new google.maps.Point(17.5, 17.5),
              }}
              clickable={false}
            />
          )}
          {!!markerRadiusMiles && (
            <Circle
              center={selected.coords}
              radius={markerRadiusMiles * MILE_TO_METERS}
              options={{
                strokeColor: theme.color.red,
                strokeWeight: 3,
                fillColor: makeTransparentColor(theme.color.red, 0.1),
                fillOpacity: 1,
                clickable: false,
              }}
            />
          )}
        </GoogleMap>
      </MapContainer>

      {!hideConfirmButton && (
        <ConfirmButton data-testid={'location-picker-confirm'} onClick={handleConfirm}>
          {buttonLabel || t('set-location')}
        </ConfirmButton>
      )}
    </Container>
  );
};
